import NiceModal, { NiceModalHandler, useModal } from '@ebay/nice-modal-react';
import { Backdrop, BackdropProps, Modal, ModalProps } from '@mui/material';
import Zoom from '@mui/material/Zoom';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FC, ReactNode } from 'react';
import type { Location } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

import { CloseOutLine } from '@/components/icons/icons';

import styles from './styles.module.less';

export interface NiceModalProps
  extends Omit<ModalProps, 'open' | 'title' | 'children'>,
    Partial<NiceModalHandler<any>> {
  chaining?: boolean; // 是否开启Promise
  title?: any; // 标题
  width?: React.CSSProperties['width']; // 宽度
  top?: React.CSSProperties['top']; // 距离顶部的距离
  bottom?: React.CSSProperties['top']; // 距离底部的距离
  contentStyle?: React.CSSProperties; // 内容样式
  closeStyle?: React.CSSProperties; // 内容样式
  maskClosable?: boolean; // 点击遮罩层是否关闭
  destroyOnClose?: boolean; // 关闭时是否销毁组件
  backdrop?: BackdropProps | Record<string, any>;
  closable?: { closeIcon?: React.ReactNode; hideClose?: boolean }; // 是否显示右上角的关闭按钮
  children?: React.ReactNode; // 内容
  duration?: number; // 持续时间
  render?: (modal: NiceModalHandler<any>, onClose?: () => void, innerProps?: any) => ReactNode;
  // 给内层组件传递的 props
  innerProps?: Record<string, any>;
  onCancel?: () => void; // 取消回调
}
export const CustomModal = NiceModal.create((props: NiceModalProps) => {
  const {
    title,
    top = '1rem',
    bottom = 'unset',
    width = '5.6rem',
    render,
    closable = {},
    contentStyle = {},
    closeStyle = {},
    backdrop = {},
    children,
    chaining = false,
    maskClosable = false,
    destroyOnClose = false,
    innerProps = {},
    ...modalProps
  } = props;
  const locationRef = useRef<Location>();
  const location = useLocation();
  const modal = useModal();
  const [visible, setVisible] = useState(modal.visible);
  useEffect(() => {
    setVisible(modal.visible);
  }, [modal.visible]);
  const onClose = useCallback(() => {
    if (chaining) {
      const modalRef = modal as any;
      modalRef.closeWithTransition = () => {
        setVisible(false);
        setTimeout(() => {
          modal.remove();
        }, 100);
      };
      modal.resolve(modalRef);
      return;
    }
    setVisible(false);
    setTimeout(() => {
      if (destroyOnClose) {
        modal.remove();
      } else {
        modal.hide();
      }
    }, 100);

    props.onCancel?.();
  }, [destroyOnClose, chaining]);
  useEffect(() => {
    locationRef.current = location;
  }, []);
  useEffect(() => {
    if (location?.pathname !== locationRef.current?.pathname) {
      modal.remove();
    }
  }, [location]);
  const HeaderClose = useMemo(() => {
    if (closable?.hideClose) {
      return (
        <div>
          <div className="closeIconContainer"></div>
        </div>
      );
    }
    if (closable?.closeIcon) {
      return (
        <div>
          <div
            className="closeIconContainer"
            onClick={() => {
              onClose();
            }}
          >
            {closable.closeIcon}
          </div>
        </div>
      );
    }
    return (
      <div>
        <CloseOutLine
          onClick={() => {
            onClose();
          }}
        />
      </div>
    );
  }, [closable]);
  return (
    <Modal {...modalProps} closeAfterTransition disableAutoFocus open={modal.visible} onClose={onClose}>
      <>
        <Backdrop
          sx={{ ...backdrop } as any}
          style={{ opacity: modalProps?.hideBackdrop ? 0 : 1 }}
          open={visible}
          onClick={() => {
            if (!maskClosable) {
              return;
            }
            onClose();
          }}
        ></Backdrop>
        <Zoom in={visible}>
          <div
            className={styles['nice-modal-container']}
            id="transition-nice-modal-container"
            style={{ width, top, bottom }}
          >
            <div className={styles['nice-modal-header']}>
              <div className={styles['nice-modal-title']}>{title}</div>
              <div className={styles['nice-modal-close']} data-testid="modal-close" style={closeStyle}>
                {HeaderClose}
              </div>
            </div>
            <div className={styles['nice-modal-content']} style={contentStyle}>
              {render ? (
                <RenderChildren
                  modal={modal}
                  render={render}
                  innerProps={innerProps}
                  onClose={() => {
                    setVisible(false);
                    setTimeout(() => {
                      if (destroyOnClose) {
                        modal.remove();
                      } else {
                        modal.hide();
                      }
                    }, 100);
                  }}
                />
              ) : (
                children
              )}
            </div>
          </div>
        </Zoom>
      </>
    </Modal>
  );
});

const RenderChildren: FC<any> = ({ render, modal, onClose, innerProps }) => {
  return <>{render(modal, onClose, innerProps)}</>;
};

const showNiceModal = (props: NiceModalProps, name = 'my-nice-modal') => {
  if (NiceModal.getModal(name)) {
    return NiceModal.show(name, props);
  }
  NiceModal.register(name, CustomModal);
  return (NiceModal as any).show(name, props);
};

export default showNiceModal;
